import { initializeApp, FirebaseApp } from 'firebase/app';
import { 
  getFirestore, 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  getDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where,
  enableIndexedDbPersistence,
  connectFirestoreEmulator,
  Timestamp,
  Firestore
} from 'firebase/firestore';
import { 
  getAuth, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged, 
  User, 
  GoogleAuthProvider, 
  signInWithPopup,
  connectAuthEmulator,
  Auth
} from 'firebase/auth';
import { validateFirebaseConfig } from './utils';
import { Feature, ProjectEstimate } from './types';

// Your web app's Firebase configuration
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

// Initialize Firebase - with error handling
let app: FirebaseApp;
let db: Firestore;
let auth: Auth;

try {
  // Validate Firebase configuration
  const { isValid, missingKeys } = validateFirebaseConfig();
  if (!isValid) {
    console.error(`Missing Firebase configuration keys: ${missingKeys.join(', ')}`);
  }
  
  app = initializeApp(firebaseConfig);
  db = getFirestore(app);
  auth = getAuth(app);
  
  // Enable offline persistence if possible
  try {
    enableIndexedDbPersistence(db).catch((err) => {
      if (err.code === 'failed-precondition') {
        console.warn('Multiple tabs open, persistence can only be enabled in one tab at a time.');
      } else if (err.code === 'unimplemented') {
        console.warn('The current browser does not support all of the features required to enable persistence');
      }
    });
  } catch (err) {
    console.warn('Error setting up persistence');
  }
  
  // Use emulators in development if configured
  if (process.env.REACT_APP_USE_FIREBASE_EMULATOR === 'true') {
    try {
      connectFirestoreEmulator(db, 'localhost', 8080);
      connectAuthEmulator(auth, 'http://localhost:9099');
    } catch (err) {
      console.error('Failed to connect to Firebase emulators');
    }
  }
} catch (error) {
  console.error("Failed to initialize Firebase:", error);
  // Create mock implementations to prevent app from crashing
  // @ts-ignore - This is a fallback for when Firebase fails to initialize
  app = {} as FirebaseApp;
  // @ts-ignore - This is a fallback for when Firebase fails to initialize
  db = {
    collection: () => ({}),
    doc: () => ({})
  } as Firestore;
  // @ts-ignore - This is a fallback for when Firebase fails to initialize
  auth = {} as Auth;
}

const googleProvider = new GoogleAuthProvider();

// Configure Google Provider
googleProvider.addScope('https://www.googleapis.com/auth/userinfo.email');
googleProvider.addScope('https://www.googleapis.com/auth/userinfo.profile');
googleProvider.setCustomParameters({
  prompt: 'select_account'
});

// Allowed domains
const ALLOWED_DOMAINS = ['sdcopartners.com', 'stitchdesignco.com'];

// Helper to convert Firebase timestamps to Date objects safely
const convertTimestampToDate = (timestamp: any): Date => {
  if (!timestamp) {
    return new Date();
  }
  if (timestamp instanceof Timestamp) {
    return timestamp.toDate();
  }
  if (timestamp.seconds) {
    return new Date(timestamp.seconds * 1000);
  }
  return new Date(timestamp);
};

// Firebase utility functions for project estimates
export const saveProjectEstimate = async (projectData: ProjectEstimate): Promise<string> => {
  try {
    // Make sure we have a slug
    if (!projectData.slug) {
      console.warn('Project data missing slug, this should not happen');
    }
    
    // Convert Date objects to Firestore Timestamps
    const firestoreData = {
      ...projectData,
      createdAt: Timestamp.fromDate(projectData.createdAt),
      updatedAt: Timestamp.fromDate(projectData.updatedAt)
    };
    
    const docRef = await addDoc(collection(db, 'projectEstimates'), firestoreData);
    return docRef.id;
  } catch (error) {
    console.error('Error saving project to Firebase:', error);
    throw error;
  }
};

export const updateProjectEstimate = async (id: string, data: Partial<ProjectEstimate>): Promise<void> => {
  try {
    // Convert Date objects to Firestore Timestamps if they exist
    const firestoreData: any = { ...data };
    if (data.createdAt) {
      firestoreData.createdAt = Timestamp.fromDate(data.createdAt);
    }
    if (data.updatedAt) {
      firestoreData.updatedAt = Timestamp.fromDate(data.updatedAt);
    } else {
      firestoreData.updatedAt = Timestamp.fromDate(new Date());
    }
    
    const projectRef = doc(db, 'projectEstimates', id);
    await updateDoc(projectRef, firestoreData);
  } catch (error) {
    console.error('Error updating project in Firebase:', error);
    throw error;
  }
};

export const deleteProjectEstimate = async (id: string): Promise<void> => {
  try {
    const projectRef = doc(db, 'projectEstimates', id);
    await deleteDoc(projectRef);
  } catch (error) {
    console.error('Error deleting project from Firebase:', error);
    throw error;
  }
};

export const getProjectEstimate = async (id: string): Promise<ProjectEstimate | null> => {
  try {
    const projectRef = doc(db, 'projectEstimates', id);
    const projectSnap = await getDoc(projectRef);
    
    if (projectSnap.exists()) {
      const data = projectSnap.data();
      const project = {
        ...data,
        id: projectSnap.id,
        createdAt: convertTimestampToDate(data.createdAt),
        updatedAt: convertTimestampToDate(data.updatedAt)
      } as ProjectEstimate;
      
      return project;
    }
    return null;
  } catch (error) {
    console.error('Error getting project from Firebase:', error);
    throw error;
  }
};

export const getAllProjectEstimates = async (): Promise<ProjectEstimate[]> => {
  try {
    const projectsSnapshot = await getDocs(collection(db, 'projectEstimates'));
    return projectsSnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        ...data,
        id: doc.id,
        createdAt: convertTimestampToDate(data.createdAt),
        updatedAt: convertTimestampToDate(data.updatedAt)
      } as ProjectEstimate;
    });
  } catch (error) {
    console.error('Error getting all projects from Firebase:', error);
    throw error;
  }
};

export const getUserProjectEstimates = async (userId: string): Promise<ProjectEstimate[]> => {
  try {
    const q = query(collection(db, 'projectEstimates'), where('userId', '==', userId));
    const projectsSnapshot = await getDocs(q);
    return projectsSnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        ...data,
        id: doc.id,
        createdAt: convertTimestampToDate(data.createdAt),
        updatedAt: convertTimestampToDate(data.updatedAt)
      } as ProjectEstimate;
    });
  } catch (error) {
    console.error('Error getting user projects from Firebase:', error);
    throw error;
  }
};

// New function to get all projects from all users
export const getAllUserProjectEstimates = async (): Promise<ProjectEstimate[]> => {
  try {
    // This gets all projects without filtering by userId
    const projectsSnapshot = await getDocs(collection(db, 'projectEstimates'));
    const projects = projectsSnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        ...data,
        id: doc.id,
        createdAt: convertTimestampToDate(data.createdAt),
        updatedAt: convertTimestampToDate(data.updatedAt)
      } as ProjectEstimate;
    });
    
    // For each project without a userEmail, try to fetch the email
    for (const project of projects) {
      if (project.userId && !project.userEmail) {
        try {
          const email = await getUserEmailById(project.userId);
          if (email) {
            project.userEmail = email;
            // Update the project in Firestore with the email
            const projectRef = doc(db, 'projectEstimates', project.id!);
            await updateDoc(projectRef, { userEmail: email });
          }
        } catch (error) {
          console.error(`Error fetching email for user ${project.userId}:`, error);
        }
      }
    }
    
    return projects;
  } catch (error) {
    console.error('Error getting all user projects from Firebase:', error);
    throw error;
  }
};

// Auth helper functions
export const signIn = async (email: string, password: string): Promise<User> => {
  try {
    const result = await signInWithEmailAndPassword(auth, email, password);
    return result.user;
  } catch (error) {
    console.error('Error signing in with email and password:', error);
    throw error;
  }
};

export const signUp = async (email: string, password: string): Promise<User> => {
  try {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    return result.user;
  } catch (error) {
    console.error('Error signing up with email and password:', error);
    throw error;
  }
};

export const signInWithGoogle = async (): Promise<User> => {
  try {
    console.log('Starting Google sign-in process');
    const result = await signInWithPopup(auth, googleProvider);
    console.log('Google sign-in successful');
    const user = result.user;
    
    // Check if user's email domain is allowed
    if (user.email) {
      const domain = user.email.split('@')[1];
      if (!ALLOWED_DOMAINS.includes(domain)) {
        console.log(`Domain ${domain} not allowed, signing out`);
        await signOut(auth);
        throw new Error(`Access restricted to ${ALLOWED_DOMAINS.join(', ')} domains only`);
      }
    } else {
      console.log('No email associated with Google account, signing out');
      await signOut(auth);
      throw new Error('No email associated with this Google account');
    }
    
    return user;
  } catch (error: any) {
    console.error('Error signing in with Google:', error);
    
    // Add more specific error handling
    if (error.code === 'auth/configuration-not-found') {
      console.error('Firebase configuration not found. Check if Google sign-in is enabled in Firebase console.');
      throw new Error('Google authentication is not properly configured. Please check your Firebase console settings.');
    }
    
    if (error.code === 'auth/popup-closed-by-user') {
      throw new Error('Sign-in popup was closed before completing the sign-in process.');
    }
    
    if (error.code === 'auth/popup-blocked') {
      throw new Error('Sign-in popup was blocked by the browser. Please allow popups for this site.');
    }
    
    throw error;
  }
};

export const logOut = async (): Promise<void> => {
  try {
    await signOut(auth);
  } catch (error) {
    console.error('Error signing out:', error);
    throw error;
  }
};

export const getCurrentUser = (): Promise<User | null> => {
  return new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      unsubscribe();
      resolve(user);
    });
  });
};

// Function to get user email by ID from Auth
export const getUserEmailById = async (userId: string): Promise<string | null> => {
  try {
    // First, check if this is the current user
    const currentUser = auth.currentUser;
    if (currentUser && currentUser.uid === userId && currentUser.email) {
      return currentUser.email;
    }
    
    // Next, check if we have a users collection in Firestore
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('uid', '==', userId));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const userData = querySnapshot.docs[0].data();
      return userData.email || null;
    }
    
    // If we couldn't find the user in our users collection,
    // we might need to create a users collection and populate it
    // with user data from Firebase Auth, but that would require
    // admin privileges which we don't have from the client
    
    return null;
  } catch (error) {
    console.error('Error getting user email by ID:', error);
    return null;
  }
};

export { auth, db }; 